import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import {
  tenantApi,
  type ScheduleCoverBlock,
  type ScheduleCoverCreateResult,
} from '../../services/tenantApi';
import { useAppDialog } from './AppDialog';
import { parseDateTimeLocalInTimezone, toDateTimeLocalInTimezone } from './scheduleTimezone';

export const COVER_REASON_MAX = 300;
export const COVER_MAX_DAYS = 90;
const DAY_MS = 86_400_000;
// The API clamps a start in the past to "now"; allow a little skew before flagging it client-side.
const PAST_TOLERANCE_MS = 5 * 60_000;

export type CoverRangeMode = 'blocks' | 'custom';

export type CoverRotationOption = { rotationId: string; name: string; groupId: string };
export type CoverUserOption = { userId: string; label: string; status?: string };

export type CoverModalInitial = {
  coveredUserId?: string;
  /** null/undefined = all rotations. */
  rotationId?: string | null;
  /** Pre-select the suggested block overlapping this instant (e.g. a clicked timeline shift). */
  focusUtc?: string;
};

export function blockKey(block: Pick<ScheduleCoverBlock, 'startUtc' | 'endUtc'>): string {
  return `${block.startUtc}|${block.endUtc}`;
}

/** Index of the suggested block containing `focusUtc`, else the first (current or next) block. */
export function pickDefaultBlockIndex(blocks: ScheduleCoverBlock[], focusUtc?: string): number {
  if (!blocks || blocks.length === 0) return -1;
  const focus = focusUtc ? Date.parse(focusUtc) : NaN;
  if (Number.isFinite(focus)) {
    const hit = blocks.findIndex(b => Date.parse(b.startUtc) <= focus && focus < Date.parse(b.endUtc));
    if (hit >= 0) return hit;
  }
  return 0;
}

/** Selected blocks must be consecutive in the suggested list; returns the spanning range. */
export function rangeFromSelectedBlocks(
  blocks: ScheduleCoverBlock[],
  selected: Set<string>,
): { startUtc?: string; endUtc?: string; error?: string } {
  const indices = (blocks || []).map((b, i) => (selected.has(blockKey(b)) ? i : -1)).filter(i => i >= 0);
  if (indices.length === 0) return { error: 'Select at least one block to cover.' };
  const first = indices[0];
  const last = indices[indices.length - 1];
  if (last - first + 1 !== indices.length) return { error: 'Select consecutive blocks only.' };
  return { startUtc: blocks[first].startUtc, endUtc: blocks[last].endUtc };
}

export function validateCover(input: {
  coveredUserId: string;
  replacementUserId: string;
  mode: CoverRangeMode;
  blocks: ScheduleCoverBlock[];
  selectedBlocks: Set<string>;
  customStart: string;
  customEnd: string;
  timezone: string;
  reason: string;
  now?: Date;
}): { startUtc?: string; endUtc?: string; errors: { covered?: string; replacement?: string; range?: string; reason?: string } } {
  const errors: { covered?: string; replacement?: string; range?: string; reason?: string } = {};
  const now = input.now ?? new Date();
  if (!input.coveredUserId) errors.covered = 'Choose the operator to cover.';
  if (!input.replacementUserId) errors.replacement = 'Choose a replacement operator.';
  else if (input.coveredUserId && sameId(input.coveredUserId, input.replacementUserId)) {
    errors.replacement = 'The replacement must be a different operator.';
  }

  let startUtc: string | undefined;
  let endUtc: string | undefined;
  if (input.mode === 'blocks') {
    const range = rangeFromSelectedBlocks(input.blocks, input.selectedBlocks);
    if (range.error) errors.range = range.error;
    else ({ startUtc, endUtc } = range);
  } else {
    const start = parseDateTimeLocalInTimezone(input.customStart, input.timezone);
    const end = parseDateTimeLocalInTimezone(input.customEnd, input.timezone);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) errors.range = 'Enter a valid start and end.';
    else if (end.getTime() <= start.getTime()) errors.range = 'End must be after start.';
    else if (end.getTime() <= now.getTime()) errors.range = 'The cover must end in the future.';
    else if (start.getTime() < now.getTime() - PAST_TOLERANCE_MS) errors.range = 'Start cannot be in the past (use Now or a block in progress).';
    else {
      startUtc = start.toISOString();
      endUtc = end.toISOString();
    }
  }
  if (startUtc && endUtc) {
    const effectiveStart = Math.max(Date.parse(startUtc), now.getTime());
    if (Date.parse(endUtc) - effectiveStart > COVER_MAX_DAYS * DAY_MS) {
      errors.range = `A cover can span at most ${COVER_MAX_DAYS} days.`;
    }
  }

  const reason = input.reason.trim();
  if (!reason) errors.reason = 'A reason is required.';
  else if (reason.length > COVER_REASON_MAX) errors.reason = `Reason must be ${COVER_REASON_MAX} characters or fewer.`;

  return { startUtc, endUtc, errors };
}

/** Group members (of every affected rotation group) first; everyone else is "other" and needs an admin force. */
export function partitionReplacementCandidates(
  users: CoverUserOption[],
  groupMemberIds: Array<Set<string>>,
  coveredUserId: string,
): { members: CoverUserOption[]; others: CoverUserOption[] } {
  const active = (users || []).filter(u => !u.status || u.status.toLowerCase() === 'active');
  const eligible = active.filter(u => !sameId(u.userId, coveredUserId));
  const inAll = (u: CoverUserOption) => groupMemberIds.length > 0 && groupMemberIds.every(set => set.has(u.userId.toLowerCase()));
  const byLabel = (a: CoverUserOption, b: CoverUserOption) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  return {
    members: eligible.filter(inAll).sort(byLabel),
    others: eligible.filter(u => !inAll(u)).sort(byLabel),
  };
}

const COVER_ERROR_HINTS: Record<string, string> = {
  nothing_to_cover: 'The covered operator holds no on-call time in that range.',
  overlapping_cover: 'This operator already has an active cover that overlaps that range.',
  replacement_not_in_group: 'The replacement is not a member of the rotation group. Only schedule managers can force this.',
  replacement_inactive: 'The replacement operator is not active.',
  range_too_long: `A cover can span at most ${COVER_MAX_DAYS} days.`,
};

export function describeCoverError(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message : String(err || '');
  if (/\(403\)/.test(message)) {
    return 'You do not have permission to manage covers for this rotation. Only schedule managers or the rotation\'s team leads can do this.';
  }
  const hint = Object.entries(COVER_ERROR_HINTS).find(([code]) => message.includes(code));
  return hint ? hint[1] : (message || fallback);
}

function sameId(a?: string | null, b?: string | null): boolean {
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

export const ScheduleCoverModal: React.FC<{
  scheduleId: string;
  timezone: string;
  rotations: CoverRotationOption[];
  /** Roster members who can be covered (union across rotations). */
  coverableUsers: CoverUserOption[];
  /** Active tenant users offered as replacements. */
  tenantUsers: CoverUserOption[];
  canForce: boolean;
  initial?: CoverModalInitial;
  onClose: () => void;
  onCreated: (result: ScheduleCoverCreateResult) => void | Promise<void>;
}> = ({ scheduleId, timezone, rotations, coverableUsers, tenantUsers, canForce, initial, onClose, onCreated }) => {
  const { confirm, dialog } = useAppDialog();
  const tz = timezone || 'UTC';
  const [coveredUserId, setCoveredUserId] = useState(initial?.coveredUserId || '');
  const [rotationId, setRotationId] = useState<string>(initial?.rotationId || '');
  const [replacementUserId, setReplacementUserId] = useState('');
  const [mode, setMode] = useState<CoverRangeMode>('blocks');
  const [blocks, setBlocks] = useState<ScheduleCoverBlock[]>([]);
  const [selectedBlocks, setSelectedBlocks] = useState<Set<string>>(new Set());
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [customStart, setCustomStart] = useState(() => toDateTimeLocalInTimezone(new Date().toISOString(), tz));
  const [customEnd, setCustomEnd] = useState(() => toDateTimeLocalInTimezone(new Date(Date.now() + 7 * DAY_MS).toISOString(), tz));
  const [reason, setReason] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState<Array<Set<string>>>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const focusUtc = useRef(initial?.focusUtc);
  const blocksSeq = useRef(0);

  const formatInTz = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return formatDateTime(date, { timeZone: tz, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const affectedRotations = useMemo(
    () => (rotationId ? rotations.filter(r => sameId(r.rotationId, rotationId)) : rotations),
    [rotations, rotationId],
  );

  useEffect(() => {
    if (!coveredUserId) {
      setBlocks([]);
      setSelectedBlocks(new Set());
      return;
    }
    const seq = ++blocksSeq.current;
    setBlocksLoading(true);
    setError(null);
    tenantApi.suggestScheduleCoverBlocks(scheduleId, coveredUserId, rotationId || null)
      .then(result => {
        if (seq !== blocksSeq.current) return;
        const list = result || [];
        setBlocks(list);
        const idx = pickDefaultBlockIndex(list, focusUtc.current);
        focusUtc.current = undefined;
        setSelectedBlocks(idx >= 0 ? new Set([blockKey(list[idx])]) : new Set());
        setMode(list.length > 0 ? 'blocks' : 'custom');
      })
      .catch(err => {
        if (seq !== blocksSeq.current) return;
        setBlocks([]);
        setSelectedBlocks(new Set());
        setMode('custom');
        setError(describeCoverError(err, 'Failed to load upcoming blocks'));
      })
      .finally(() => { if (seq === blocksSeq.current) setBlocksLoading(false); });
  }, [scheduleId, coveredUserId, rotationId]);

  useEffect(() => {
    let cancelled = false;
    const groupIds = Array.from(new Set(affectedRotations.map(r => r.groupId).filter(Boolean)));
    Promise.all(groupIds.map(id => tenantApi.getGroupMembers(id).catch(() => [])))
      .then(lists => {
        if (cancelled) return;
        setGroupMemberIds(lists.map(list => new Set((list || []).map(m => m.userId.toLowerCase()))));
      });
    return () => { cancelled = true; };
  }, [affectedRotations]);

  const candidates = useMemo(
    () => partitionReplacementCandidates(tenantUsers, groupMemberIds, coveredUserId),
    [tenantUsers, groupMemberIds, coveredUserId],
  );
  const replacementNeedsForce = Boolean(replacementUserId) && !candidates.members.some(u => sameId(u.userId, replacementUserId));

  const validation = validateCover({
    coveredUserId, replacementUserId, mode, blocks, selectedBlocks, customStart, customEnd, timezone: tz, reason,
  });
  const showErrors = touched;

  const toggleBlock = (key: string) => {
    setSelectedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const submit = async () => {
    setTouched(true);
    if (Object.keys(validation.errors).length > 0) return;
    if (replacementNeedsForce) {
      if (!canForce) {
        setError(COVER_ERROR_HINTS.replacement_not_in_group);
        return;
      }
      const ok = await confirm(
        'The replacement is not a member of the rotation group. Force this cover anyway? This is recorded in the audit log.',
        { title: 'Force cover', confirmLabel: 'Force cover', danger: true },
      );
      if (!ok) return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await tenantApi.createScheduleCover(scheduleId, {
        coveredUserId,
        replacementUserId,
        rotationId: rotationId || null,
        startUtc: validation.startUtc,
        endUtc: validation.endUtc,
        reason: reason.trim(),
        force: replacementNeedsForce || undefined,
      });
      await onCreated(result);
    } catch (err) {
      setError(describeCoverError(err, 'Failed to create cover'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal modal-schedule-cover"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-cover-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="schedule-cover-title">Cover rotation</h3>
          <p className="help-text">Replace an operator for whole rotation blocks (for example illness or leave). Covers apply immediately, without an approval step, and override any swaps in the range.</p>

          <div className="form-row" style={{ flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: '1 1 220px' }}>
              <label htmlFor="cover-covered-user">Operator to cover</label>
              <select id="cover-covered-user" value={coveredUserId} onChange={(e) => setCoveredUserId(e.target.value)}>
                <option value="">Select operator…</option>
                {coverableUsers.map(u => <option key={u.userId} value={u.userId}>{u.label}</option>)}
              </select>
              {validation.errors.covered && showErrors && <div className="error-text" role="alert">{validation.errors.covered}</div>}
            </div>
            <div className="form-group" style={{ flex: '1 1 220px' }}>
              <label htmlFor="cover-rotation">Rotation</label>
              <select id="cover-rotation" value={rotationId} onChange={(e) => setRotationId(e.target.value)}>
                <option value="">All rotations (recommended)</option>
                {rotations.map(r => <option key={r.rotationId} value={r.rotationId}>{r.name}</option>)}
              </select>
            </div>
          </div>

          <fieldset className="form-group" style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 600, marginBottom: 4 }}>Range ({tz})</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: 6 }}>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="radio" name="cover-range-mode" value="blocks" checked={mode === 'blocks'} disabled={blocks.length === 0} onChange={() => setMode('blocks')} />
                Rotation blocks
              </label>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="radio" name="cover-range-mode" value="custom" checked={mode === 'custom'} onChange={() => setMode('custom')} />
                Custom start/end
              </label>
            </div>
            {mode === 'blocks' && (
              blocksLoading ? <div className="help-text">Loading upcoming blocks…</div>
                : !coveredUserId ? <div className="help-text">Choose an operator to see their upcoming blocks.</div>
                : blocks.length === 0 ? <div className="help-text">No upcoming blocks for this operator.</div>
                : (
                  <div className="cover-block-list" style={{ display: 'grid', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
                    {blocks.map(b => {
                      const key = blockKey(b);
                      return (
                        <label key={key} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input type="checkbox" checked={selectedBlocks.has(key)} onChange={() => toggleBlock(key)} />
                          <span>
                            {formatInTz(b.startUtc)} – {formatInTz(b.endUtc)}
                            {b.rotationName ? ` · ${b.rotationName}` : ''}
                            {b.inProgress ? ' · in progress' : ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )
            )}
            {mode === 'custom' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <label style={{ display: 'grid', gap: 2 }}>
                  Start
                  <input aria-label="Cover start" type="datetime-local" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                </label>
                <label style={{ display: 'grid', gap: 2 }}>
                  End
                  <input aria-label="Cover end" type="datetime-local" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                </label>
              </div>
            )}
            {validation.errors.range && (showErrors || mode === 'custom') && <div className="error-text" role="alert">{validation.errors.range}</div>}
          </fieldset>

          <div className="form-group">
            <label htmlFor="cover-replacement">Replacement operator</label>
            <select id="cover-replacement" value={replacementUserId} onChange={(e) => setReplacementUserId(e.target.value)}>
              <option value="">Select replacement…</option>
              <optgroup label="Rotation group members">
                {candidates.members.map(u => <option key={u.userId} value={u.userId}>{u.label}</option>)}
              </optgroup>
              {canForce && candidates.others.length > 0 && (
                <optgroup label="Other tenant users (requires force)">
                  {candidates.others.map(u => <option key={u.userId} value={u.userId}>{u.label}</option>)}
                </optgroup>
              )}
            </select>
            {replacementNeedsForce && canForce && (
              <div className="badge badge-warning" style={{ marginTop: 6, display: 'inline-block' }}>
                ⚠ Not in the rotation group – this cover will be forced and audited.
              </div>
            )}
            {validation.errors.replacement && showErrors && <div className="error-text" role="alert">{validation.errors.replacement}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="cover-reason">Reason</label>
            <textarea
              id="cover-reason"
              value={reason}
              maxLength={COVER_REASON_MAX}
              rows={2}
              placeholder="For example: sick leave"
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            <div className="help-text">{reason.trim().length}/{COVER_REASON_MAX}</div>
            {validation.errors.reason && showErrors && <div className="error-text" role="alert">{validation.errors.reason}</div>}
          </div>

          {error && <div className="error-banner" role="alert" style={{ marginTop: 8 }}>{error}</div>}

          <div className="form-actions" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="button" className="btn-primary" onClick={submit} disabled={submitting || blocksLoading}>
              {submitting ? 'Creating…' : 'Create cover'}
            </button>
          </div>
        </div>
      </div>
      {dialog}
    </>
  );
};
